import React, { useState } from 'react';
import { 
  Email,
  Phone,
  LocationOn,
  AccessTime,
  Send,
  WhatsApp,
  Facebook,
  Instagram,
  Telegram,
  Business,
  SupportAgent
} from '@mui/icons-material';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // In real app, you would send to your backend
      console.log('Form submitted:', formData);
      
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      
      // Reset status after 5 seconds
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
      
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <Email />,
      title: "البريد الإلكتروني",
      value: "support@bazaar-marketplace.com",
      link: "mailto:support@bazaar-marketplace.com",
      description: "للشكاوى والاستفسارات العامة"
    },
    {
      icon: <Phone />,
      title: "الهاتف / واتساب",
      value: "+20 150 332 3006",
      link: "https://wa.me/201503323006",
      description: "متاح من 10 صباحًا حتى 10 مساءً"
    },
    {
      icon: <LocationOn />,
      title: "العنوان",
      value: "القاهرة، مصر",
      link: "https://maps.google.com/?q=Cairo,+Egypt",
      description: "مقر الشركة الرئيسي"
    },
    {
      icon: <AccessTime />,
      title: "ساعات العمل",
      value: "10 صباحًا - 10 مساءً",
      link: "",
      description: "طوال أيام الأسبوع (بتوقيت مصر)"
    }
  ];

  const socialLinks = [
    {
      icon: <Facebook />,
      name: "Facebook",
      url: "https://www.facebook.com/profile.php?id=61580674130494",
      color: "hover:bg-blue-600"
    },
    {
      icon: <Instagram />,
      name: "Instagram",
      url: "https://www.instagram.com/bazaar_marketplace/",
      color: "hover:bg-gradient-to-r hover:from-purple-600 hover:via-pink-600 hover:to-orange-500"
    },
    {
      icon: <WhatsApp />,
      name: "WhatsApp",
      url: "https://wa.me/201503323006",
      color: "hover:bg-green-600"
    },
    {
      icon: <Telegram />,
      name: "Telegram",
      url: "https://t.me/bazaarmarketplace",
      color: "hover:bg-blue-500"
    }
  ];

  const subjects = [
    "استفسار عام",
    "مشكلة في الطلب",
    "دعم فني",
    "تسجيل كبائع",
    "شكوى",
    "اقتراح تحسين",
    "طلب شراكة"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white" dir="rtl">
      <div className="max-w-6xl mx-auto p-4 sm:p-6 lg:p-8">
        
        {/* Header Section */}
        <div className="mb-12 text-center">
          <div className="inline-flex items-center justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-primary to-primary-dark rounded-2xl blur-lg opacity-30"></div>
              <div className="relative bg-gradient-to-r from-primary to-primary-dark p-5 rounded-2xl shadow-xl">
                <SupportAgent className="text-white text-4xl" />
              </div>
            </div>
          </div>
          
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            اتصل بنا
          </h1>
          
          <div className="h-1.5 w-32 bg-gradient-to-r from-primary to-primary-dark mx-auto rounded-full mb-4"></div>
          
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            نحن هنا لمساعدتك! تواصل مع فريق الدعم للحصول على المساعدة أو الإجابة عن استفساراتك
          </p>
        </div>

        {/* Contact Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {contactInfo.map((info, index) => (
            <a
              key={index}
              href={info.link}
              target={info.link ? "_blank" : "_self"}
              rel="noopener noreferrer"
              className={`bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 ${
                info.link ? 'hover:scale-105 cursor-pointer' : ''
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-gradient-to-r from-primary/10 to-primary/20">
                  <span className="text-primary text-xl">{info.icon}</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-2">{info.title}</h3>
                  <p className="text-lg text-gray-800 font-medium mb-1">{info.value}</p>
                  <p className="text-sm text-gray-500">{info.description}</p>
                </div>
              </div>
            </a>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white rounded-2xl shadow-lg p-6 lg:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 rounded-lg bg-gradient-to-r from-primary/10 to-primary/20">
                <Send className="text-primary text-xl" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">أرسل رسالة</h2>
            </div>

            {submitStatus === 'success' && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-700 font-medium">
                  ✓ تم إرسال رسالتك بنجاح! سوف نرد عليك في أقرب وقت ممكن.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 font-medium">
                  ⚠️ حدث خطأ في الإرسال. يرجى المحاولة مرة أخرى أو التواصل عبر واتساب.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    الاسم الكامل *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full p-3 rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300"
                    placeholder="أدخل اسمك الكامل"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    البريد الإلكتروني *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full p-3 rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300"
                    placeholder="example@email.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    رقم الهاتف
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full p-3 rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300"
                    placeholder="01XXXXXXXXX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    موضوع الرسالة *
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full p-3 rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300"
                  >
                    <option value="">اختر الموضوع</option>
                    {subjects.map((subject, index) => (
                      <option key={index} value={subject}>
                        {subject}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الرسالة *
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full p-3 rounded-lg border border-gray-300 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-300"
                  placeholder="اكتب رسالتك هنا..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 px-6 bg-gradient-to-r from-primary to-primary-dark text-white font-medium rounded-lg hover:opacity-90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    جاري الإرسال...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Send />
                    إرسال الرسالة
                  </span>
                )}
              </button>
            </form>
          </div>

          {/* Social & Additional Info */}
          <div>
            {/* Social Media */}
            <div className="bg-white rounded-2xl shadow-lg p-6 lg:p-8 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 rounded-lg bg-gradient-to-r from-primary/10 to-primary/20">
                  <Business className="text-primary text-xl" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">تواصل معنا عبر</h2>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center justify-center gap-2 p-4 rounded-xl bg-gray-50 transition-all duration-300 ${social.color} hover:text-white hover:scale-105`}
                  >
                    <span className="text-xl">{social.icon}</span>
                    <span className="font-medium">{social.name}</span>
                  </a>
                ))}
              </div>

              <p className="text-gray-600 text-center">
                تابعنا على وسائل التواصل الاجتماعي للحصول على أحدث العروض والأخبار
              </p>
            </div>

            {/* FAQ Quick Link */}
            <div className="bg-gradient-to-r from-primary/10 to-primary/20 rounded-2xl p-6 lg:p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                ✨ ربما تجد إجابتك هنا
              </h3>
              <p className="text-gray-700 mb-4">
                العديد من الأسئلة الشائعة تمت الإجابة عليها في قسم الأسئلة الشائعة
              </p>
              <a
                href="/faq"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary font-medium rounded-lg hover:bg-primary hover:text-white transition-all duration-300"
              >
                <span>تصفح الأسئلة الشائعة</span>
                <span>←</span>
              </a>
            </div>

            {/* Company Info */}
            <div className="mt-6 p-6 bg-gray-900 rounded-2xl text-white">
              <h3 className="font-bold text-lg mb-3">معلومات الشركة</h3>
              <div className="space-y-2 text-gray-300">
                <p>الاسم: <strong className="text-white">بازار ماركت بليس</strong></p>
                <p>رقم ضريبي: <strong className="text-white">752-382-462</strong></p>
                <p>السجل التجاري: <strong className="text-white">123456</strong></p>
                <p>المطور: <strong className="text-primary">CODEOCEAN.TECH</strong></p>
              </div>
            </div>
          </div>
        </div>

        {/* Map Placeholder */}
        <div className="mt-12 bg-gray-100 rounded-2xl p-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <LocationOn className="text-primary text-2xl" />
            <h3 className="text-2xl font-bold text-gray-900">موقعنا</h3>
          </div>
          <div className="bg-white p-4 rounded-xl border border-gray-300">
            <div className="h-64 bg-gray-200 rounded-lg flex items-center justify-center">
              <p className="text-gray-500">خريطة الموقع سوف تظهر هنا</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <div className="text-gray-500 text-sm leading-relaxed max-w-2xl mx-auto space-y-2">
            <p>
              متوسط وقت الرد: 4 ساعات عمل خلال ساعات الدوام
            </p>
            <p className="text-gray-400">
              جميع الحقوق محفوظة © {new Date().getFullYear()} بازار ماركت بليس
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;